import React, { useState, useRef, useEffect, useCallback } from "react";

/* ─── Constants ─────────────────────────────────── */
const CLASS_OPTIONS = ["NURSERY", "PP-1", "PP-2", "1ST CLASS", "2ND CLASS", "3RD CLASS"];
const SECTION_OPTIONS = ["A", "B", "C", "D"];

const SEED = [
  { id: 1, name: "ENGLISH", assignments: [{ cls: "NURSERY", sec: "B" }, { cls: "PP-1", sec: "A" }, { cls: "1ST CLASS", sec: "A" }], createdTime: "2024-06-25 17:21:11", paperHTML: "" },
  { id: 2, name: "MATHS", assignments: [{ cls: "NURSERY", sec: "B" }, { cls: "PP-2", sec: "A" }, { cls: "2ND CLASS", sec: "A" }], createdTime: "2024-06-25 17:21:40", paperHTML: "" },
  { id: 3, name: "EVS", assignments: [{ cls: "PP-1", sec: "A" }, { cls: "PP-2", sec: "B" }], createdTime: "2024-06-25 17:21:53", paperHTML: "" },
  { id: 5, name: "HINDI", assignments: [{ cls: "PP-1", sec: "A" }, { cls: "1ST CLASS", sec: "A" }], createdTime: "2024-06-25 17:22:21", paperHTML: "" },
  { id: 6, name: "ART & CRAFT ORAL", assignments: [{ cls: "NURSERY", sec: "A" }, { cls: "2ND CLASS", sec: "B" }], createdTime: "2025-07-25 11:53:31", paperHTML: "" },
];

/* ─── Load Quill from CDN ───────────────────────── */
function useQuillReady() {
  const [ready, setReady] = useState(!!window.Quill);

  useEffect(() => {
    if (window.Quill) { setReady(true); return; }

    // Load Quill CSS
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://cdnjs.cloudflare.com/ajax/libs/quill/1.3.7/quill.snow.min.css";
    document.head.appendChild(link);

    // Load Quill JS
    const script = document.createElement("script");
    script.src = "https://cdnjs.cloudflare.com/ajax/libs/quill/1.3.7/quill.min.js";
    script.onload = () => setReady(true);
    document.head.appendChild(script);
  }, []);

  return ready;
}

/* ─── Quill Rich Editor ─────────────────────────── */
function RichEditor({ initialHTML, onChangeRef }) {
  const quillReady = useQuillReady();
  const containerRef = useRef(null);
  const quillRef = useRef(null);

  useEffect(() => {
    if (!quillReady || !containerRef.current || quillRef.current) return;

    const quill = new window.Quill(containerRef.current, {
      theme: "snow",
      placeholder: "Write your question paper here...",
      modules: {
        toolbar: [
          [{ header: [1, 2, 3, 4, false] }],
          [{ font: [] }],
          [{ size: ["small", false, "large", "huge"] }],
          ["bold", "italic", "underline", "strike"],
          [{ color: [] }, { background: [] }],
          [{ script: "sub" }, { script: "super" }],
          [{ list: "ordered" }, { list: "bullet" }],
          [{ indent: "-1" }, { indent: "+1" }],
          [{ align: [] }],
          ["blockquote", "code-block"],
          ["link", "image"],
          ["clean"],
        ],
      },
    });

    // Set initial content
    if (initialHTML) {
      quill.root.innerHTML = initialHTML;
    }

    // Keep ref updated on every change
    quill.on("text-change", () => {
      if (onChangeRef) onChangeRef.current = quill.root.innerHTML;
    });

    quillRef.current = quill;
  }, [quillReady]);

  if (!quillReady) {
    return (
      <div style={{ height: 380, display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #dee2e6", borderRadius: 6, background: "#f8f9fa" }}>
        <div className="text-muted">Loading editor…</div>
      </div>
    );
  }

  return (
    <div style={{ borderRadius: 6, overflow: "hidden", border: "1px solid #dee2e6" }}>
      <div ref={containerRef} style={{ height: 380, fontSize: 14 }} />
    </div>
  );
}

/* ─── Tiny modal shell ──────────────────────────── */
function AppModal({ show, title, size = "lg", onClose, children }) {
  if (!show) return null;
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 1055, overflowY: "auto", display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: 60 }}>
      <div style={{ width: "100%", maxWidth: size === "xl" ? 960 : 700, background: "#fff", borderRadius: 8, boxShadow: "0 8px 32px rgba(0,0,0,.25)", marginBottom: 60 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px", borderBottom: "1px solid #d4d4d4" }}>
          <strong style={{ fontSize: 15,}}>{title}</strong>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", lineHeight: 1, color: "#666" }}>×</button>
        </div>
        <div style={{ padding: 24 }}>{children}</div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   SUBJECT MODAL
═══════════════════════════════════════════════════ */
function SubjectModal({ show, onClose, onSave, existing }) {
  const [name, setName] = useState("");
  const [selCls, setSelCls] = useState("");
  const [selSec, setSelSec] = useState("");
  const [chips, setChips] = useState([]);

  useEffect(() => {
    if (show) {
      setName(existing?.name || "");
      setChips(existing?.assignments ? existing.assignments.map(a => ({ cls: a.cls, sec: a.sec })) : []);
      setSelCls("");
      setSelSec("");
    }
  }, [show]);

  const addChip = () => {
    if (!selCls || !selSec) return;
    if (chips.some(c => c.cls === selCls && c.sec === selSec)) return;
    setChips(prev => [...prev, { cls: selCls, sec: selSec }]);
    setSelCls("");
    setSelSec("");
  };

  const removeChip = idx => setChips(prev => prev.filter((_, i) => i !== idx));

  const save = () => {
    if (!name.trim()) { alert("Enter a subject name."); return; }
    if (!chips.length) { alert("Add at least one class & section."); return; }
    onSave({ name: name.trim().toUpperCase(), assignments: chips });
  };

  return (
    <AppModal show={show} title={existing ? "Edit Subject" : "Add Subject"} onClose={onClose}>
      <div className="mb-3">
        <label className="form-label fw-semibold">Subject Name</label>
        <input className="form-control" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. MATHEMATICS" />
      </div>

      <div className="mb-3">
        <label className="form-label fw-semibold">Assign Class &amp; Section</label>
        <div className="row g-2 align-items-end">
          <div className="col">
            <label className="form-label small mb-1">Class</label>
            <select className="form-select" value={selCls} onChange={e => setSelCls(e.target.value)}>
              <option value="">Select Class</option>
              {CLASS_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="col">
            <label className="form-label small mb-1">Section</label>
            <select className="form-select" value={selSec} onChange={e => setSelSec(e.target.value)}>
              <option value="">Select Section</option>
              {SECTION_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="col-auto">
            <button type="button" className="btn-add" onClick={addChip}>+ Add</button>
          </div>
        </div>
      </div>

      {chips.length > 0 && (
        <div className="mb-4">
          <label className="form-label small fw-semibold text-muted">Assigned</label>
          <div className="d-flex flex-wrap gap-2">
            {chips.map((ch, i) => (
              <span key={i} style={{ background: "#e8f5e9", color: "#2e7d32", border: "1px solid #a5d6a7", borderRadius: 20, padding: "4px 12px", fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
                {ch.cls} – {ch.sec}
                <span style={{ cursor: "pointer", color: "#c62828", fontWeight: "bold" }} onClick={() => removeChip(i)}>×</span>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="d-flex justify-content-center">
        <button type="button" className="btn btn-primary px-4" onClick={save}>
          {existing ? "Update" : "Submit"}
        </button>
      </div>
    </AppModal>
  );
}

/* ═══════════════════════════════════════════════════
   CREATE PAPER MODAL  — Quill powered
═══════════════════════════════════════════════════ */
function PaperModal({ show, subject, onClose, onSave }) {
  const latestHTML = useRef("");

  useEffect(() => {
    if (show) latestHTML.current = subject?.paperHTML || "";
  }, [show, subject]);

  if (!show || !subject) return null;

  return (
    <AppModal show={show} title={`Create Paper – ${subject.name}`} size="xl" onClose={onClose}>
      {/* Paper meta header */}
      <div style={{ background: "#f8f9fa", border: "1px solid #dee2e6", borderRadius: 6, padding: "12px 16px", marginBottom: 16 }}>
        <div className="row g-2 align-items-center">
          <div className="col-auto">
            <span className="badge" style={{ background: "#20c997", fontSize: 12 }}>Subject: {subject.name}</span>
          </div>
          <div className="col-auto">
            <span className="badge bg-secondary" style={{ fontSize: 12 }}>
              Classes: {subject.assignments.map(a => `${a.cls} ${a.sec}`).join(", ")}
            </span>
          </div>
          <div className="col-auto ms-auto text-muted small">
            {new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" })}
          </div>
        </div>
      </div>

      <RichEditor initialHTML={subject.paperHTML || ""} onChangeRef={latestHTML} />

      <div className="d-flex justify-content-end gap-2 mt-3">
        <button className="btn btn-outline-secondary" onClick={onClose}>Cancel</button>
        <button className="btn text-white px-4" style={{ background: "#20c997" }} onClick={() => onSave(latestHTML.current)}>
          💾 Save Paper
        </button>
      </div>
    </AppModal>
  );
}

/* ─── PDF Download ──────────────────────────────── */
function downloadPDF(subject) {
  const win = window.open("", "_blank");
  win.document.write(`<!DOCTYPE html><html><head>
    <title>${subject.name} – Question Paper</title>
    <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/quill/1.3.7/quill.snow.min.css"/>
    <style>
      body  { font-family: Arial, sans-serif; margin: 48px; color: #111; }
      h1    { font-size: 22px; border-bottom: 2px solid #333; padding-bottom: 8px; margin-bottom: 4px; }
      .meta { font-size: 12px; color: #777; margin-bottom: 28px; }
      .ql-editor { padding: 0; }
      @media print { body { margin: 20mm; } }
    </style>
  </head><body>
    <h1>${subject.name} — Question Paper</h1>
    <p class="meta">Date: ${new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" })} &nbsp;|&nbsp; Classes: ${subject.assignments.map(a => `${a.cls} ${a.sec}`).join(", ")}</p>
    <div class="ql-editor">${subject.paperHTML}</div>
  </body></html>`);
  win.document.close();
  setTimeout(() => { win.focus(); win.print(); win.close(); }, 600);
}

/* ═══════════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════════ */
export default function SubjectList() {
  const [subjects, setSubjects] = useState(SEED);
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(10);

  const [subjectModal, setSubjectModal] = useState({ open: false, existing: null });
  const [paperModal, setPaperModal] = useState({ open: false, subjectId: null });

  const openAdd = () => setSubjectModal({ open: true, existing: null });
  const openEdit = (subj) => setSubjectModal({ open: true, existing: subj });
  const closeSubjectModal = () => setSubjectModal({ open: false, existing: null });

  const openPaper = (subj) => setPaperModal({ open: true, subjectId: subj.id });
  const closePaper = () => setPaperModal({ open: false, subjectId: null });

  const saveSubject = useCallback(({ name, assignments }) => {
    setSubjects(prev => {
      if (subjectModal.existing) {
        return prev.map(s => s.id === subjectModal.existing.id ? { ...s, name, assignments } : s);
      }
      const id = Date.now();
      return [...prev, { id, name, assignments, createdTime: new Date().toISOString().replace("T", " ").slice(0, 19), paperHTML: "" }];
    });
    closeSubjectModal();
  }, [subjectModal]);

  const savePaper = useCallback((html) => {
    setSubjects(prev => prev.map(s => s.id === paperModal.subjectId ? { ...s, paperHTML: html } : s));
    closePaper();
  }, [paperModal.subjectId]);

  const deleteSubject = (id) => setSubjects(prev => prev.filter(s => s.id !== id));

  const paperSubject = subjects.find(s => s.id === paperModal.subjectId);

  const filtered = subjects
    .filter(s => s.name.toLowerCase().includes(search.toLowerCase()))
    .slice(0, pageSize);

  return (
    <div className="card">
      {/* Header */}
      <div className="card-header">
        <h5>List of Subjects</h5>
        <button className="btn-add" onClick={openAdd}>
          + ADD Subject
        </button>
      </div>

      <div className="card-body">
        {/* Controls */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center gap-2">
            <label className="mb-0 small fw-semibold">Show</label>
            <select className="form-select form-select-sm" style={{ width: 70 }} value={pageSize} onChange={e => setPageSize(+e.target.value)}>
              {[5, 10, 25, 50].map(n => <option key={n}>{n}</option>)}
            </select>
            <span className="small fw-semibold">entries</span>
          </div>
          <div className="d-flex align-items-center gap-2">
            <label className="mb-0 small">Search:</label>
            <input className="form-control form-control-sm" style={{ width: 200 }} value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>

        {/* Table */}
        <div className="table-responsive">
          <table className="table table-bordered align-top">
            <thead className="table-light">
              <tr>
                <th>Sr.No</th>
                <th>Subject Name</th>
                <th>Classes &amp; Sections</th>
                <th>Created Time</th>
                <th>Create Paper</th>
                <th>Download Paper</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length ? filtered.map((subj, idx) => (
                <tr key={subj.id}>
                  <td>{idx + 1}</td>
                  <td className="fw-semibold">{subj.name}</td>
                  <td>
                    <div className="d-flex flex-wrap gap-1">
                      {subj.assignments.map((a, i) => (
                        <span key={i} style={{ background: "#e3f2fd", color: "#1565c0", border: "1px solid #90caf9", borderRadius: 12, padding: "2px 10px", fontSize: 12 }}>
                          {a.cls} {a.sec}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="text-muted small" style={{ whiteSpace: "nowrap" }}>{subj.createdTime}</td>
                  <td>
                    <button className="btn btn-sm text-white" style={{ background: "#0d6efd", whiteSpace: "nowrap" }} onClick={() => openPaper(subj)}>
                      📄 Create Paper
                    </button>
                  </td>
                  <td>
                    {subj.paperHTML
                      ? <button className="btn btn-sm text-white" style={{ background: "#28a745", whiteSpace: "nowrap" }} onClick={() => downloadPDF(subj)}>⬇ Download PDF</button>
                      : <span className="text-muted small">No paper yet</span>
                    }
                  </td>
                  <td>
                    <div className="d-flex gap-2">
                      <button className="btn btn-sm text-white" style={{ background: "#28a745" }} onClick={() => openEdit(subj)}><i className="bi bi-pencil-square"></i></button>
                      <button className="btn btn-sm btn-danger" onClick={() => deleteSubject(subj.id)}><i className="bi bi-trash"></i></button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={7} className="text-center text-muted py-4">No subjects found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <SubjectModal
        show={subjectModal.open}
        existing={subjectModal.existing}
        onClose={closeSubjectModal}
        onSave={saveSubject}
      />

      <PaperModal
        show={paperModal.open}
        subject={paperSubject}
        onClose={closePaper}
        onSave={savePaper}
      />
    </div>
  );
}